import { useEffect, useRef, useCallback } from 'react';
import KEYCLOAK_CONFIG from '../config/keycloakConfig';

// Refresh interval: 4 dakika (access token 5 dk olduğu için 1 dk önce yenile)
const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

// JWT token'ın exp claim'ini parse et
function getTokenExp(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 =
      parts[1].replace(/-/g, "+").replace(/_/g, "/") +
      "==".slice(2 - (parts[1].length % 4));
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json).exp || null;
  } catch {
    return null;
  }
}

// Refresh token süresi dolmuş mu kontrol et
function isRefreshTokenExpired(refreshToken) {
  const exp = getTokenExp(refreshToken);
  if (!exp) return true;
  // 30 saniye margin bırak
  return Date.now() / 1000 > exp - 30;
}

/**
 * Keycloak SSO session hala aktifse sessiz re-authentication yap
 * iframe ile prompt=none kullanarak yeni authorization code alır
 */
function silentReAuthenticate() {
  return new Promise((resolve, reject) => {
    const state = Math.random().toString(36).substring(2);
    const redirectUri = KEYCLOAK_CONFIG.redirectUri;

    const authUrl =
      `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/auth` +
      `?client_id=${encodeURIComponent(KEYCLOAK_CONFIG.clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=openid` +
      `&prompt=none` +
      `&state=${state}`;

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Silent auth timeout"));
    }, 10000);

    function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }

    function onMessage(event) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "keycloak-silent-auth") {
        cleanup();
        if (event.data.code) {
          resolve(event.data.code);
        } else {
          reject(new Error(event.data.error || "Silent auth failed"));
        }
      }
    }

    window.addEventListener("message", onMessage);
    iframe.src = authUrl;
  });
}

/**
 * Authorization code'u token'a çevir
 */
async function exchangeCodeForTokens(code) {
  const params = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: KEYCLOAK_CONFIG.clientId,
    code: code,
    redirect_uri: KEYCLOAK_CONFIG.redirectUri,
  });

  const response = await fetch(
    `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params,
    }
  );

  return response.json();
}

/**
 * Yeni token'ı companyName ile token exchange yap
 */
async function tokenExchangeForClient(subjectToken, companyName) {
  const params = new URLSearchParams({
    grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
    client_id: KEYCLOAK_CONFIG.clientId,
    subject_token: subjectToken,
    audience: companyName,
    requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
  });

  const response = await fetch(
    `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params,
    }
  );

  return response.json();
}

/**
 * Token yenileme hook'u
 * Periyodik olarak refresh token kullanarak access token'ı yeniler
 * Refresh token süresi dolduysa Keycloak SSO session üzerinden sessiz re-authentication dener
 */
export const useTokenRefresh = () => {
  const intervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Token'ı yenile
  const refreshTokens = useCallback(async () => {
    console.log("[TokenRefresh] === REFRESH ATTEMPT ===");
    console.log("[TokenRefresh] Timestamp:", new Date().toISOString());

    // Eğer zaten yenileme yapılıyorsa, tekrar yapma
    if (isRefreshingRef.current) {
      console.log("[TokenRefresh] Already refreshing, skipping...");
      return false;
    }

    const refreshToken = localStorage.getItem("refreshToken");
    const companyName = localStorage.getItem("companyName");

    console.log("[TokenRefresh] refreshToken exists:", !!refreshToken);
    console.log("[TokenRefresh] companyName:", companyName);

    if (!refreshToken) {
      console.log("[TokenRefresh] No refresh token found - ABORTING");
      return false;
    }

    isRefreshingRef.current = true;

    try {
      // Refresh token süresi dolmuş mu kontrol et
      if (isRefreshTokenExpired(refreshToken)) {
        console.log("[TokenRefresh] Refresh token expired, trying silent re-authentication...");
        return await handleSilentReAuth(companyName);
      }

      // Normal refresh flow - refresh token her zaman DefaultClient'a bağlı
      console.log("[TokenRefresh] Refresh token still valid, doing normal refresh...");

      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: KEYCLOAK_CONFIG.clientId,
        refresh_token: refreshToken,
      });

      console.log("[TokenRefresh] Using client_id:", KEYCLOAK_CONFIG.clientId);

      const response = await fetch(`${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params
      });

      const data = await response.json();

      if (data.access_token) {
        console.log("[TokenRefresh] DefaultClient refresh SUCCESS");

        // Refresh token'ı hemen güncelle
        if (data.refresh_token) {
          localStorage.setItem("refreshToken", data.refresh_token);
        }

        // companyName varsa token exchange yap
        if (companyName && companyName !== KEYCLOAK_CONFIG.clientId) {
          console.log("[TokenRefresh] Doing token exchange for client:", companyName);
          const exchangeData = await tokenExchangeForClient(data.access_token, companyName);

          if (exchangeData.access_token) {
            localStorage.setItem("authToken", exchangeData.access_token);
            if (exchangeData.refresh_token) {
              localStorage.setItem("refreshToken", exchangeData.refresh_token);
            }
            console.log("[TokenRefresh] Token exchange SUCCESS");
            return true;
          } else {
            console.error("[TokenRefresh] Token exchange FAILED:", exchangeData.error);
            // Exchange başarısız olsa da DefaultClient token'ını kullan
            localStorage.setItem("authToken", data.access_token);
            return true;
          }
        } else {
          localStorage.setItem("authToken", data.access_token);
          return true;
        }
      } else {
        console.error("[TokenRefresh] FAILED:", data.error, data.error_description);

        // invalid_grant ise sessiz re-auth dene
        if (data.error === 'invalid_grant') {
          console.log("[TokenRefresh] invalid_grant - trying silent re-authentication...");
          return await handleSilentReAuth(companyName);
        }

        return false;
      }
    } catch (error) {
      console.error("[TokenRefresh] EXCEPTION during refresh:", error);
      return false;
    } finally {
      isRefreshingRef.current = false;
      console.log("[TokenRefresh] === REFRESH COMPLETE ===");
    }
  }, []);

  // Sessiz re-authentication
  const handleSilentReAuth = async (companyName) => {
    try {
      const code = await silentReAuthenticate();
      console.log("[TokenRefresh] Silent auth got code, exchanging for tokens...");

      const tokenData = await exchangeCodeForTokens(code);

      if (!tokenData.access_token) {
        throw new Error("No access_token from silent auth");
      }

      // id_token'ı kaydet
      if (tokenData.id_token) {
        localStorage.setItem("idToken", tokenData.id_token);
      }

      // companyName varsa token exchange yap (doğru client kapsamı için)
      if (companyName && companyName !== KEYCLOAK_CONFIG.clientId) {
        console.log("[TokenRefresh] Doing token exchange for client:", companyName);
        const exchangeData = await tokenExchangeForClient(tokenData.access_token, companyName);

        if (exchangeData.access_token) {
          localStorage.setItem("authToken", exchangeData.access_token);
          if (exchangeData.refresh_token) {
            localStorage.setItem("refreshToken", exchangeData.refresh_token);
          }
          console.log("[TokenRefresh] Silent re-auth + token exchange SUCCESS");
          return true;
        } else {
          throw new Error("Token exchange failed: " + (exchangeData.error_description || exchangeData.error));
        }
      } else {
        // companyName yoksa direkt kaydet
        localStorage.setItem("authToken", tokenData.access_token);
        if (tokenData.refresh_token) {
          localStorage.setItem("refreshToken", tokenData.refresh_token);
        }
        console.log("[TokenRefresh] Silent re-auth SUCCESS");
        return true;
      }
    } catch (error) {
      console.error("[TokenRefresh] Silent re-auth FAILED:", error.message);
      // SSO session da dolmuş, login'e yönlendir
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      window.location.href = '/login';
      return false;
    }
  };

  // Interval'ı başlat
  const startRefreshInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    console.log(`[TokenRefresh] Starting refresh interval (${REFRESH_INTERVAL_MS / 60000} minutes)`);

    intervalRef.current = setInterval(() => {
      refreshTokens();
    }, REFRESH_INTERVAL_MS);
  }, [refreshTokens]);

  // Interval'ı durdur
  const stopRefreshInterval = useCallback(() => {
    if (intervalRef.current) {
      console.log("[TokenRefresh] Stopping refresh interval");
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Component mount olduğunda interval'ı başlat
  useEffect(() => {
    console.log("[TokenRefresh] === HOOK MOUNT ===");
    const authToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (authToken && refreshToken) {
      console.log("[TokenRefresh] Both tokens exist, starting refresh interval");
      startRefreshInterval();
    } else {
      console.log("[TokenRefresh] Missing tokens, NOT starting interval");
    }

    // Cleanup
    return () => {
      stopRefreshInterval();
    };
  }, [startRefreshInterval, stopRefreshInterval]);

  // Visibility change event - sayfa tekrar aktif olduğunda token'ı kontrol et
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const authToken = localStorage.getItem("authToken");
        const refreshToken = localStorage.getItem("refreshToken");

        if (authToken && refreshToken) {
          console.log("[TokenRefresh] Page visible, triggering refresh...");
          refreshTokens();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshTokens]);

  return {
    refreshTokens,
    startRefreshInterval,
    stopRefreshInterval,
  };
};

export default useTokenRefresh;
